import React from 'react';
import { flushSync } from 'react-dom';
import { Grid } from 'semantic-ui-react';
import cx from 'classnames';
import {
  SearchInput,
  Facets,
  FilterList,
} from '@plone/volto/components/manage/Blocks/Search/components';
import AnoTituloFacet from './AnoTituloFacet';

// Wrapper de cada facet (copia o padrão do core TopSideFacets, mas dentro do
// card .ag-lista). Só usado quando o bloco tem data.facets configurado.
const FacetWrapper = ({ children, facetSettings = {} }) => {
  const { field = {} } = facetSettings;
  return (
    <Grid.Column
      mobile={12}
      tablet={6}
      computer={4}
      className={cx('facet', {
        [`facet-index-${field.value}`]: !!field.value,
      })}
    >
      {children}
    </Grid.Column>
  );
};

// O SearchBlockView do Volto (applyDefaults) devolve à listagem todo critério
// da consulta base que a busca não traz. No filtro de ano, o critério de título
// da base é só o ano em que a lista abre: com o filtro em "Todos" (sem valor)
// ele voltava, e a lista ficava no ano de abertura. Aqui o critério de cada
// filtro de ano sai sempre do valor escolhido no filtro.
export const listagemComFiltroDeAno = (listingData, data, facets = {}) => {
  const filtros = (data?.facets || []).filter(
    (facet) => facet?.type === 'agsusAnoTitulo' && facet.field?.value,
  );
  if (!filtros.length || !Array.isArray(listingData?.query)) {
    return listingData;
  }
  const campos = new Set(filtros.map((facet) => facet.field.value));
  return {
    ...listingData,
    query: [
      ...listingData.query.filter((criterio) => !campos.has(criterio.i)),
      ...filtros
        .map((facet) =>
          AnoTituloFacet.valueToQuery({
            value: facets?.[facet.field.value],
            facet,
          }),
        )
        .filter(Boolean),
    ],
  };
};

// Search variation DS — card único: faixa azul clara (headline) + (opcional)
// filtros por facet + campo de busca (restrito à query base do bloco) +
// resultados (children = ListingBody com a listing variation agsusListaDS).
//
// Facets só renderizam quando data.facets tem itens — as 9 listas simples de
// transparência usam facets:[] e seguem sem filtro, exatamente como antes.
// Em /processos-seletivos o facet de Subject (KeywordIndex nativo) filtra por
// status (Aberto/Encerrado/Cancelado) sem nenhum backend custom.
const AgsusListaLayout = (props) => {
  const {
    children,
    data,
    facets,
    setFacets,
    onTriggerSearch,
    searchedText,
    querystring = {},
    isEditMode,
  } = props;
  const showInput = data.showSearchInput !== false;
  const isLive = !data.showSearchButton;
  const hasFacets = data.facets?.length > 0;

  const applyFacets = (f) => {
    flushSync(() => {
      setFacets(f);
      onTriggerSearch(searchedText || '', f);
    });
  };

  return (
    <div className="ag-lista">
      <div className="ag-lista__panel">
        {data.headline && (
          <div className="ag-lista__header">{data.headline}</div>
        )}
        {hasFacets && (
          <div className="ag-lista__facets">
            <Grid verticalAlign="bottom" columns={12}>
              <Facets
                data={data}
                querystring={querystring}
                facets={facets}
                setFacets={applyFacets}
                facetWrapper={FacetWrapper}
                isEditMode={isEditMode}
              />
            </Grid>
            <FilterList
              {...props}
              isEditMode={isEditMode}
              setFacets={applyFacets}
            />
          </div>
        )}
        {showInput && (
          <div className="ag-lista__search">
            <SearchInput {...props} isLive={isLive} />
          </div>
        )}
        {React.isValidElement(children) && children.props?.data
          ? React.cloneElement(children, {
              data: listagemComFiltroDeAno(children.props.data, data, facets),
            })
          : children}
      </div>
    </div>
  );
};

export default AgsusListaLayout;
